import React, { useState, useEffect } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { Check, Calendar, Clock, Video } from "lucide-react";
import { InlineWidget } from "react-calendly";
import { PRODUCTS } from "../data/products";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Calendly public scheduling URL (set REACT_APP_CALENDLY_URL in frontend/.env)
const CALENDLY_URL = process.env.REACT_APP_CALENDLY_URL || "";
const CALENDLY_CONFIGURED = CALENDLY_URL && CALENDLY_URL.trim().length > 0;

const PROJECT_TYPES = [
  "Oil, Gas & Energy",
  "Power & Utilities",
  "Infrastructure",
  "Metals, Mining & Minerals",
  "Chemicals",
  "Manufacturing & Marine",
  "Other",
];

export function CtaForm() {
  const [mode, setMode] = useState("form"); // 'form' | 'calendar'
  const [form, setForm] = useState({
    name: "", email: "", company: "", project_type: "", platforms: [], message: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  // Pre-populate platforms multi-select from `?p=<slug>[,<slug>...]` query param
  // (e.g., /contact?p=luminex or /contact?p=luminex,nirnaya). Slugs map → p.indo labels.
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const raw = params.get("p");
    if (!raw) return;
    const slugs = raw.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean);
    const labels = slugs
      .map((slug) => PRODUCTS.find((p) => p.slug === slug)?.indo)
      .filter(Boolean);
    if (labels.length === 0) return;
    setForm((f) => {
      const merged = Array.from(new Set([...(f.platforms || []), ...labels]));
      return { ...f, platforms: merged };
    });
    // Smoothly bring the form into view so the visitor sees their selection
    setMode("form");
    // eslint-disable-next-line
  }, [location.search]);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const togglePlatform = (label) => {
    setForm((f) => {
      const has = f.platforms.includes(label);
      return { ...f, platforms: has ? f.platforms.filter((p) => p !== label) : [...f.platforms, label] };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name || !form.email || !form.company) {
      setError("Please fill in name, email, and company.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      setSuccess(true);
      setForm({ name: "", email: "", company: "", project_type: "", platforms: [], message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="cta-band reveal" id="contact" data-testid="contact-section">
      <div className="container">
        <div className="cta-inner">
          <div className="cta-bg">
            <svg viewBox="0 0 800 400" preserveAspectRatio="xMidYMid slice">
              <defs>
                <radialGradient id="ctag1" cx="20%" cy="30%">
                  <stop offset="0%" stopColor="#F26B1F" stopOpacity=".25" />
                  <stop offset="100%" stopColor="#F26B1F" stopOpacity="0" />
                </radialGradient>
                <radialGradient id="ctag2" cx="80%" cy="70%">
                  <stop offset="0%" stopColor="#1E4E8C" stopOpacity=".45" />
                  <stop offset="100%" stopColor="#1E4E8C" stopOpacity="0" />
                </radialGradient>
                <radialGradient id="ctag3" cx="55%" cy="45%">
                  <stop offset="0%" stopColor="#2E70AD" stopOpacity=".3" />
                  <stop offset="100%" stopColor="#2E70AD" stopOpacity="0" />
                </radialGradient>
              </defs>
              <rect width="800" height="400" fill="url(#ctag1)" />
              <rect width="800" height="400" fill="url(#ctag2)" />
              <rect width="800" height="400" fill="url(#ctag3)" />
            </svg>
          </div>
          <div className="cta-copy">
            <div className="eyebrow">Ready when you are</div>
            <h2 data-testid="contact-h2">
              Turn your capex<br />
              into <em>cognition.</em>
            </h2>
            <p>
              Thirty minutes with a Capexara architect. A live walkthrough of the platform stack mapped to your most complex active project.
            </p>
            <div className="cta-meta" data-testid="contact-meta">
              <span>Unit No 603-604, 6th Floor, Tower B</span>
              <span>Bhutani Alphathum, Nepz Post Office</span>
              <span>Gautam Buddha Nagar, Noida, UP 201305, India</span>
              <span style={{ marginTop: 8 }}>
                <a href="mailto:contact@capexara.com" style={{ color: "inherit" }}>contact@capexara.com</a>
              </span>
            </div>
          </div>

          <div>
            <div className="contact-tabs" data-testid="contact-tabs">
              <button
                className={"contact-tab " + (mode === "form" ? "active" : "")}
                onClick={() => setMode("form")}
                data-testid="contact-tab-form"
                type="button"
              >
                Send a brief
              </button>
              <button
                className={"contact-tab " + (mode === "calendar" ? "active" : "")}
                onClick={() => setMode("calendar")}
                data-testid="contact-tab-calendar"
                type="button"
              >
                Book a slot
              </button>
            </div>

            {mode === "form" ? (
              <form className="lead-form" onSubmit={submit} data-testid="lead-form">
                <div className="lead-form-title">Request a demo</div>
                <div className="lead-form-sub">Tell us about your most complex active project.</div>

                {success ? (
                  <div className="lead-success" data-testid="lead-success-message">
                    <Check size={18} style={{ flexShrink: 0, marginTop: 2 }} />
                    <div>
                      <strong>Thank you.</strong> A Capexara architect will be in touch within one business day. Prefer to pick a time directly? Switch to "Book a slot".
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="lead-row">
                      <div className="lead-field">
                        <label className="lead-label">Name</label>
                        <input className="lead-input" type="text" value={form.name} onChange={update("name")} placeholder="Aseem Rai" required data-testid="lead-input-name" />
                      </div>
                      <div className="lead-field">
                        <label className="lead-label">Work email</label>
                        <input className="lead-input" type="email" value={form.email} onChange={update("email")} placeholder="you@company.com" required data-testid="lead-input-email" />
                      </div>
                    </div>
                    <div className="lead-field">
                      <label className="lead-label">Company</label>
                      <input className="lead-input" type="text" value={form.company} onChange={update("company")} placeholder="Acme EPC" required data-testid="lead-input-company" />
                    </div>
                    <div className="lead-field">
                      <label className="lead-label">Industry</label>
                      <select className="lead-select" value={form.project_type} onChange={update("project_type")} data-testid="lead-select-industry">
                        <option value="">Select industry…</option>
                        {PROJECT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div className="lead-field">
                      <label className="lead-label">Platforms of interest <span className="lead-label-hint">(optional · pick one or more)</span></label>
                      <div className="lead-platforms" data-testid="lead-platforms">
                        {PRODUCTS.map((p) => {
                          const checked = form.platforms.includes(p.indo);
                          return (
                            <button
                              type="button"
                              key={p.slug}
                              className={"lead-platform-pill " + (checked ? "checked" : "")}
                              onClick={() => togglePlatform(p.indo)}
                              aria-pressed={checked}
                              data-testid={`lead-platform-${p.slug}`}
                            >
                              <span className="lead-platform-check">{checked && <Check size={11} strokeWidth={3} />}</span>
                              <span className="lead-platform-name">{p.indo}</span>
                              <span className="lead-platform-sub">{p.name.split(/[&·]/)[0].trim()}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div className="lead-field">
                      <label className="lead-label">Tell us about your project</label>
                      <textarea className="lead-textarea" value={form.message} onChange={update("message")} placeholder="Scope, size, timeline, current pain-points…" rows={3} data-testid="lead-textarea-message" />
                    </div>
                    {error && <div className="lead-error" data-testid="lead-error">{error}</div>}
                    <button className="lead-submit" type="submit" disabled={loading} data-testid="lead-submit-button">
                      {loading ? "Submitting…" : "Request a walkthrough →"}
                    </button>
                  </>
                )}
              </form>
            ) : (
              <div className="cal-embed" data-testid="cal-embed">
                {CALENDLY_CONFIGURED ? (
                  <InlineWidget
                    url={CALENDLY_URL}
                    styles={{ width: "100%", height: "680px", minHeight: "680px" }}
                    prefill={{
                      name: form.name || undefined,
                      email: form.email || undefined,
                      customAnswers: form.company ? { a1: form.company } : undefined,
                    }}
                    utm={{
                      utmSource: "capexara-site",
                      utmMedium: "contact-page",
                      utmCampaign: "book-a-slot",
                    }}
                    pageSettings={{
                      backgroundColor: "0F131C",
                      primaryColor: "F26B1F",
                      textColor: "F3F4F6",
                      hideEventTypeDetails: true,
                      hideLandingPageDetails: true,
                      hideGdprBanner: true,
                    }}
                    data-testid="calendly-inline-widget"
                  />
                ) : (
                  <div className="cal-fallback" data-testid="cal-fallback">
                    <div className="cal-fallback-icon">
                      <Calendar size={36} strokeWidth={1.4} />
                    </div>
                    <div className="cal-fallback-title">Schedule a 30-min walkthrough</div>
                    <p className="cal-fallback-desc">
                      Our live booking calendar will be embedded right here once our scheduling link is published. In the meantime, reach out directly — a Capexara architect will respond within one business day.
                    </p>
                    <div className="cal-fallback-features">
                      <span className="cal-fallback-feat"><Clock size={14} /> 30 minutes</span>
                      <span className="cal-fallback-feat"><Video size={14} /> Video call</span>
                    </div>
                    <a href="mailto:contact@capexara.com?subject=30-min%20walkthrough%20request" className="btn btn-primary" data-testid="cal-fallback-email">
                      Email us to book <span className="arrow">→</span>
                    </a>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
